import styles from './ActionsBarMobile.module.scss';

import { Close, TableRows } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FastForwardIcon from '@mui/icons-material/FastForward';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TuneIcon from '@mui/icons-material/Tune';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Badge, IconButton, Stack } from '@mui/material';
import Fab from '@mui/material/Fab';
import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { Search } from '@work4all/components/lib/input/actions/search/Search';
import { IActionConfig } from '@work4all/components/lib/input/actions/types';
import { getLinkData } from '@work4all/components/lib/input/base-action-button/utils';

import { useTableLayoutState } from '../../../../../table-layout';

import {
  ActionConfig,
  ActionsMenuMobile,
  ActionType,
} from './ActionsMenuMobile';

interface ActionsBarMobileV8Props {
  searchFilter: string;
  onSearchFilter: (e: string) => void;
  canFilter: boolean;
  openFilterOverlay: () => void;
  mappedActions: Record<ActionType, ActionConfig | null>;
  filtersCount: number;
  mobile?: boolean;
  mobileMenuButtonRef: RefObject<HTMLButtonElement>;
  hideMoreButton?: boolean;
  hideSearch?: boolean;
  isSomeRowsSelected: boolean;
  onResetSelectedRows: () => void;
}

export const ActionsBarMobileV8: React.FC<ActionsBarMobileV8Props> = (
  props
) => {
  const {
    searchFilter,
    onSearchFilter,
    canFilter,
    openFilterOverlay,
    mappedActions,
    filtersCount,
    mobile,
    mobileMenuButtonRef,
    hideMoreButton,
    hideSearch,
    isSomeRowsSelected,
    onResetSelectedRows,
  } = props;

  const { t } = useTranslation();
  const [layoutValue] = useTableLayoutState();

  function getButtonProps(
    action: ActionConfig
  ): React.ComponentPropsWithoutRef<typeof Fab> {
    const { linkProps } = getLinkData(action.href);

    return {
      size: 'medium',
      onClick: action.onClick,
      disabled: action.disabled,
      ...linkProps,
    };
  }

  return (
    <>
      <Stack p="0.5rem" pb="1rem" gap="1rem">
        <div className={styles.mobileFilters}>
          {!hideSearch && (
            <Search
              value={searchFilter}
              onChange={(e) => onSearchFilter(e?.target.value || '')}
              fieldStyle="fill"
            />
          )}

          <IconButton
            color="primary"
            onClick={(mappedActions.columns as IActionConfig).onClick}
            sx={{ flex: 'none' }}
          >
            {layoutValue === 'cards' ? <TableRows /> : <ViewColumnIcon />}
          </IconButton>

          {!hideMoreButton && canFilter && (
            <ActionsMenuMobile
              buttonRef={mobileMenuButtonRef}
              {...(mappedActions as Partial<
                Record<ActionType, ActionConfig | null>
              >)}
            />
          )}
        </div>

        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          gap="1rem"
        >
          <div className={styles.selectedFilters}>
            {(mappedActions.filter as IActionConfig)?.children}
          </div>

          <IconButton
            color="primary"
            onClick={() => openFilterOverlay()}
            sx={{ flex: 'none' }}
          >
            <Badge badgeContent={mobile ? 0 : filtersCount} color="warning">
              <TuneIcon />
            </Badge>
          </IconButton>
        </Stack>
      </Stack>

      <div className={styles.mobileToolbar}>
        <div className={styles.inner}>
          {isSomeRowsSelected && (
            <Fab size="medium" onClick={onResetSelectedRows}>
              <Close />
            </Fab>
          )}
        </div>
        <div className={styles.inner}>
          {mappedActions.billing ? (
            <Fab color="primary" {...getButtonProps(mappedActions.billing)}>
              <ReceiptIcon />
            </Fab>
          ) : null}

          {mappedActions.edit ? (
            <Tooltip
              activateForDisabled={{ activeateOnClick: true }}
              title={
                mappedActions.edit.disabled ? t('RIGHTS.MISSING') : undefined
              }
            >
              <Fab {...getButtonProps(mappedActions.edit)}>
                <EditIcon />
              </Fab>
            </Tooltip>
          ) : null}

          {mappedActions.add ? (
            <Tooltip
              activateForDisabled={{ activeateOnClick: true }}
              title={
                mappedActions.add.disabled ? t('RIGHTS.MISSING') : undefined
              }
            >
              <Fab
                ref={
                  mappedActions.add
                    ?.rootRef as React.RefObject<HTMLButtonElement>
                }
                color="primary"
                {...getButtonProps(mappedActions.add)}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          ) : null}

          {mappedActions.convert ? (
            <Fab {...getButtonProps(mappedActions.convert)}>
              <FastForwardIcon />
            </Fab>
          ) : null}
        </div>
      </div>
    </>
  );
};
